import { MAX_TILE_ID } from "../document/packed-tile.ts";
import type { TileRef } from "../document/types.ts";
import { asArray, asBoolean, asInteger, asIntegerInRange, asRecord, asString } from "../document/validate.ts";
import type { GroundKind, GroundRule } from "./ground-at.ts";
import type { BrushFlip } from "./scatter-entries.ts";
import { fileText } from "./template-file.ts";
import { TEMPLATE_ID, templateNameProblem, type PropTemplate } from "./templates.ts";

/**
 * The brush set file (ADR-123 decision 1): a named list of entries the prop brush scatters, one set per file,
 * plain JSON with a schema version, written with a fixed key order so equal sets are equal bytes, and read back
 * with every value checked and every key known, the first problem named by its path. A set belongs to no world:
 * a frame or template it names that a world lacks is flagged where the set is used (`brushSetFlags`), never
 * refused by the reader, so a load never rewrites the file.
 */

/**
 * The layout this build writes, and the highest it reads: schema 2 adds the set's own ground, schema 3 the zone
 * ground kind, schema 4 the set's three room and cover options, an entry's `groundCover` and the tile ground kind;
 * schemas 1 to 3, without them, are still read.
 */
export const BRUSH_SET_SCHEMA_VERSION = 4;

/**
 * The first schema whose ground kinds may name a zone.
 */
const ZONE_KINDS_SCHEMA = 3;

/**
 * The first schema that holds the set's three options, an entry's `groundCover` and a ground kind of one tile.
 */
const ROOM_SCHEMA = 4;

/**
 * What the library says of a set written at a schema this build does not know, and what a route says when an
 * older editor's write would replace it (decision 1.4).
 */
export const BRUSH_SET_NEWER = "Made by a newer editor; reload";

/**
 * The most entries a set holds.
 */
export const MAX_BRUSH_ENTRIES = 32;

/**
 * The largest weight an entry carries.
 */
export const MAX_BRUSH_WEIGHT = 1000;

/**
 * The largest gap an entry keeps, in whole pixels.
 */
export const MAX_BRUSH_GAP = 1024;

/**
 * The most ground kinds one entry's filter names.
 */
export const MAX_GROUND_KINDS = 32;

/**
 * The longest tileset or terrain name a set holds, in UTF-16 code units.
 */
const MAX_NAME_UNITS = 256;

/**
 * The largest terrain colour a ground kind names.
 */
const MAX_COLOUR = 255;

/**
 * Every value of `BrushFlip`, the axes an entry may be mirrored on at random.
 */
export const BRUSH_FLIPS: readonly BrushFlip[] = ["none", "x", "y", "xy"];

/**
 * What every entry carries: how often it is picked, the room it keeps in whole pixels and its ground filter.
 */
interface BrushEntryCommon {
  readonly weight: number;
  readonly gap: number;
  readonly ground?: GroundRule;
}

/**
 * One entry of a set: a prop frame, which may be mirrored and which may say whether it is ground cover (absent,
 * automatic: cover when the frame blocks no cell, ADR-123's amendment of 2026-10-13, A2.1), or a template placed
 * whole, which is neither mirrored nor cover.
 */
export type BrushEntry = (BrushEntryCommon & { readonly kind: "frame"; readonly frame: TileRef; readonly flip: BrushFlip; readonly groundCover?: boolean }) | (BrushEntryCommon & { readonly kind: "template"; readonly template: string; readonly flip: "none" });

/**
 * A brush set: its name, its own ground rule, which every entry's foot point passes before the entry's own (absent,
 * anywhere), whether it also keeps its gap from props that are not its own (`keepsGapFromOthers`), whether it may
 * stand on other props (`allowsPlacingOnTop`), whether it clears the ground cover under what it places
 * (`clearsGroundCover`; never true with `allowsPlacingOnTop`), and its entries, in the author's order. A set saved
 * before schema 4 reads all three options false.
 */
export interface BrushSet {
  readonly name: string;
  readonly ground?: GroundRule;
  readonly keepsGapFromOthers: boolean;
  readonly allowsPlacingOnTop: boolean;
  readonly clearsGroundCover: boolean;
  readonly entries: readonly BrushEntry[];
}

/**
 * Why an entry cannot be used in a world: its frame is not in the world's packs, its template is gone, its
 * template's file does not read, or its template carries a behaviour (decisions 1.3 and 1.5).
 */
export type BrushSetFlag = "frame-missing" | "template-missing" | "template-broken" | "template-behaviour";

/**
 * What `brushSetFlags` asks of the world and the library.
 */
export interface BrushSetLookup {
  /** Whether the world's catalogue holds a prop frame. */
  readonly hasFrame: (frame: TileRef) => boolean;
  /** The template of an id, `broken` when it is listed but fails to read, or null when there is none. */
  readonly template: (id: string) => PropTemplate | "broken" | null;
}

/**
 * Writes a set at this build's schema: the schema version, the name, the set's ground, its three options, always
 * written, and the entries, each entry's keys in a fixed order, a ground, the set's or an entry's, left out when
 * there is none, and a `groundCover` left out unless the entry states it. A set that allows placing on top is
 * written with `clearsGroundCover` false, since nothing is removed then (A3.1).
 *
 * @param set The set.
 * @returns The file's text, two-space indented, ending in a newline.
 */
export function serialiseBrushSet(set: BrushSet): string {
  const ground = set.ground === undefined ? {} : { ground: plainGround(set.ground) };
  return fileText({
    schemaVersion: BRUSH_SET_SCHEMA_VERSION,
    name: set.name,
    ...ground,
    keepsGapFromOthers: set.keepsGapFromOthers,
    allowsPlacingOnTop: set.allowsPlacingOnTop,
    clearsGroundCover: set.clearsGroundCover && !set.allowsPlacingOnTop,
    entries: set.entries.map(plainEntry),
  });
}

/**
 * An entry as plain data with a fixed key order.
 *
 * @param entry The entry.
 * @returns The plain object.
 */
function plainEntry(entry: BrushEntry): Record<string, unknown> {
  const named = entry.kind === "frame" ? { frame: { tileset: entry.frame.tileset, tile: entry.frame.tile } } : { template: entry.template };
  const cover = entry.kind === "frame" && entry.groundCover !== undefined ? { groundCover: entry.groundCover } : {};
  const ground = entry.ground === undefined ? {} : { ground: plainGround(entry.ground) };
  return { kind: entry.kind, ...named, weight: entry.weight, gap: entry.gap, flip: entry.flip, ...cover, ...ground };
}

/**
 * A ground rule as plain data with a fixed key order.
 *
 * @param rule The rule.
 * @returns The plain object.
 */
function plainGround(rule: GroundRule): Record<string, unknown> {
  return { mode: rule.mode, kinds: rule.kinds.map(plainGroundKind) };
}

/**
 * A ground kind as plain data with a fixed key order.
 *
 * @param kind The kind.
 * @returns The plain object.
 */
function plainGroundKind(kind: GroundKind): Record<string, unknown> {
  if ("terrain" in kind) {
    return { terrain: kind.terrain, colour: kind.colour };
  }
  if ("tileset" in kind) {
    return kind.tile === undefined ? { tileset: kind.tileset } : { tileset: kind.tileset, tile: kind.tile };
  }
  return { zone: kind.zone, world: kind.world };
}

/**
 * The schema a set's text declares, read without reading the set, so a route can tell a newer set it holds from
 * an older editor's write over it.
 *
 * @param text The file's text.
 * @returns The declared schema, or null when the text is not a JSON object with a whole number `schemaVersion`.
 */
export function brushSetSchemaVersion(text: string): number | null {
  let parsed: unknown;
  try {
    parsed = JSON.parse(text);
  } catch {
    return null;
  }
  if (typeof parsed !== "object" || parsed === null || Array.isArray(parsed)) {
    return null;
  }
  const version = (parsed as Record<string, unknown>)["schemaVersion"];
  return typeof version === "number" && Number.isSafeInteger(version) ? version : null;
}

/**
 * Why a write must not replace a stored set (decision 1.4): the stored set is of a schema this build does not
 * write, or of a higher schema than the write declares, as an older tab's write over a set with its own ground
 * would be, so an older editor never strips a field. A stored text whose schema cannot be read does not refuse.
 *
 * @param stored The stored set's text.
 * @param incoming The write's text, already read by `readBrushSet`.
 * @returns `BRUSH_SET_NEWER`, or null when the write may replace the stored set.
 */
export function brushSetReplaceRefusal(stored: string, incoming: string): string | null {
  const held = brushSetSchemaVersion(stored) ?? 0;
  return held > Math.min(BRUSH_SET_SCHEMA_VERSION, brushSetSchemaVersion(incoming) ?? 0) ? BRUSH_SET_NEWER : null;
}

/**
 * Reads a set.
 *
 * @param text The file's text.
 * @returns The set.
 * @throws Error naming the path of the first problem: not JSON, a schema this build does not read (a newer one
 *   with `BRUSH_SET_NEWER`), an unknown key (a set's own ground at schema 1, or a schema 4 field in an older file,
 *   among them), a bad name, an option that is not a boolean, `allowsPlacingOnTop` and `clearsGroundCover` both
 *   true, a malformed ground, too few or too many entries, a malformed entry, or a frame named by two frame
 *   entries.
 */
export function readBrushSet(text: string): BrushSet {
  let parsed: unknown;
  try {
    parsed = JSON.parse(text);
  } catch {
    throw new Error("a brush set file must be JSON");
  }
  const record = asRecord(parsed, "brushSet");
  const version = asInteger(record["schemaVersion"], "brushSet.schemaVersion");
  if (version > BRUSH_SET_SCHEMA_VERSION) {
    throw new Error(`brushSet.schemaVersion: ${BRUSH_SET_NEWER} (schema ${version}; this build reads schema ${BRUSH_SET_SCHEMA_VERSION})`);
  }
  if (version < 1) {
    throw new Error(`brushSet.schemaVersion: brush sets of schema ${version} are not read by this build, which reads schema 1 to ${BRUSH_SET_SCHEMA_VERSION}`);
  }
  const options = version >= ROOM_SCHEMA ? BRUSH_SET_OPTIONS : [];
  knownKeys(record, version === 1 ? ["schemaVersion", "name", "entries"] : ["schemaVersion", "name", "ground", ...options, "entries"], "brushSet");
  const name = asString(record["name"], "brushSet.name");
  const problem = templateNameProblem(name, "brush set");
  if (problem !== null) {
    throw new Error(`brushSet.name: ${problem}`);
  }
  const entries = asArray(record["entries"], "brushSet.entries");
  if (entries.length === 0 || entries.length > MAX_BRUSH_ENTRIES) {
    throw new Error(`brushSet.entries holds 1 to ${MAX_BRUSH_ENTRIES} entries, not ${entries.length}`);
  }
  const ground = record["ground"] === undefined ? {} : { ground: readGround(record["ground"], version, "brushSet.ground") };
  const keepsGapFromOthers = readOption(record["keepsGapFromOthers"], "brushSet.keepsGapFromOthers");
  const allowsPlacingOnTop = readOption(record["allowsPlacingOnTop"], "brushSet.allowsPlacingOnTop");
  const clearsGroundCover = readOption(record["clearsGroundCover"], "brushSet.clearsGroundCover");
  if (allowsPlacingOnTop && clearsGroundCover) {
    throw new Error("brushSet.clearsGroundCover cannot be true while brushSet.allowsPlacingOnTop is true, since nothing is removed when props may stand on each other");
  }
  const frames = new Map<string, number>();
  return {
    name,
    ...ground,
    keepsGapFromOthers,
    allowsPlacingOnTop,
    clearsGroundCover,
    entries: entries.map((value, i) => {
      const path = `brushSet.entries[${i}]`;
      const entry = readEntry(value, version, path);
      if (entry.kind === "frame") {
        const key = `${entry.frame.tileset}\u0000${entry.frame.tile}`;
        const first = frames.get(key);
        if (first !== undefined) {
          throw new Error(`${path}.frame: ${entry.frame.tileset} tile ${entry.frame.tile} is already entry ${first}'s frame`);
        }
        frames.set(key, i);
      }
      return entry;
    }),
  };
}

/**
 * The keys of the set's three options, in the order they are written.
 */
const BRUSH_SET_OPTIONS: readonly string[] = ["keepsGapFromOthers", "allowsPlacingOnTop", "clearsGroundCover"];

/**
 * Reads one of the set's options, absent read as false.
 *
 * @param value Candidate value.
 * @param path Path for error messages.
 * @returns The option.
 * @throws Error when the value is present and not a boolean.
 */
function readOption(value: unknown, path: string): boolean {
  return value === undefined ? false : asBoolean(value, path);
}

/**
 * Refuses a key outside a list, `__proto__` included.
 *
 * @param record The object.
 * @param allowed The keys it may hold.
 * @param path Path for error messages.
 * @throws Error naming the first unknown key's path.
 */
function knownKeys(record: Record<string, unknown>, allowed: readonly string[], path: string): void {
  for (const key of Object.keys(record)) {
    if (!allowed.includes(key)) {
      throw new Error(`${path}.${key} is not a field of this schema`);
    }
  }
}

/**
 * Reads a whole number within bounds.
 *
 * @param value Candidate value.
 * @param min The least.
 * @param max The most.
 * @param path Path for error messages.
 * @returns The number.
 * @throws Error when the value is not a whole number from `min` to `max`.
 */
function wholeIn(value: unknown, min: number, max: number, path: string): number {
  const number = asInteger(value, path);
  if (number < min || number > max) {
    throw new Error(`${path} must be a whole number from ${min} to ${max}`);
  }
  return number;
}

/**
 * Reads a name of 1 to `MAX_NAME_UNITS` code units.
 *
 * @param value Candidate value.
 * @param path Path for error messages.
 * @returns The name.
 * @throws Error when the value is not such a string.
 */
function shortName(value: unknown, path: string): string {
  const text = asString(value, path);
  if (text.length === 0 || text.length > MAX_NAME_UNITS) {
    throw new Error(`${path} is 1 to ${MAX_NAME_UNITS} characters`);
  }
  return text;
}

/**
 * Reads one entry.
 *
 * @param value Candidate value.
 * @param version The file's schema, which says which ground kinds it may hold.
 * @param path Path for error messages.
 * @returns The entry.
 * @throws Error naming the first problem's path, a `groundCover` on a template entry or at a schema before 4 among
 *   them.
 */
function readEntry(value: unknown, version: number, path: string): BrushEntry {
  const record = asRecord(value, path);
  const kind = asString(record["kind"], `${path}.kind`);
  if (kind !== "frame" && kind !== "template") {
    throw new Error(`${path}.kind is frame or template, not ${kind}`);
  }
  knownKeys(record, kind === "frame" && version >= ROOM_SCHEMA ? ["kind", kind, "weight", "gap", "flip", "groundCover", "ground"] : ["kind", kind, "weight", "gap", "flip", "ground"], path);
  const weight = wholeIn(record["weight"], 1, MAX_BRUSH_WEIGHT, `${path}.weight`);
  const gap = wholeIn(record["gap"], 0, MAX_BRUSH_GAP, `${path}.gap`);
  const flip = readFlip(record["flip"], `${path}.flip`);
  const ground = record["ground"] === undefined ? {} : { ground: readGround(record["ground"], version, `${path}.ground`) };
  if (kind === "frame") {
    const cover = record["groundCover"] === undefined ? {} : { groundCover: asBoolean(record["groundCover"], `${path}.groundCover`) };
    return { kind, frame: readFrame(record["frame"], `${path}.frame`), weight, gap, flip, ...cover, ...ground };
  }
  const template = asString(record["template"], `${path}.template`);
  if (!TEMPLATE_ID.test(template)) {
    throw new Error(`${path}.template is a template id: 1 to 64 lower case letters, digits and -, starting with a letter or digit`);
  }
  if (flip !== "none") {
    throw new Error(`${path}.flip is none for a template, which is placed as it was made`);
  }
  return { kind, template, weight, gap, flip, ...ground };
}

/**
 * Reads an entry's flip, absent read as `none`.
 *
 * @param value Candidate value.
 * @param path Path for error messages.
 * @returns The flip.
 * @throws Error when the value is not one of `BRUSH_FLIPS`.
 */
function readFlip(value: unknown, path: string): BrushFlip {
  if (value === undefined) {
    return "none";
  }
  const flip = asString(value, path);
  const known = BRUSH_FLIPS.find((one) => one === flip);
  if (known === undefined) {
    throw new Error(`${path} is one of ${BRUSH_FLIPS.join(", ")}, not ${flip}`);
  }
  return known;
}

/**
 * Reads a frame entry's frame: a tileset and a local tile id, without flips.
 *
 * @param value Candidate value.
 * @param path Path for error messages.
 * @returns The frame.
 * @throws Error naming the first problem's path.
 */
function readFrame(value: unknown, path: string): TileRef {
  const record = asRecord(value, path);
  knownKeys(record, ["tileset", "tile"], path);
  return { tileset: shortName(record["tileset"], `${path}.tileset`), tile: asIntegerInRange(record["tile"], MAX_TILE_ID, `${path}.tile`) };
}

/**
 * Reads a ground rule, an entry's or the set's.
 *
 * @param value Candidate value.
 * @param version The file's schema, which says which ground kinds it may hold.
 * @param path Path for error messages.
 * @returns The filter.
 * @throws Error naming the first problem's path.
 */
function readGround(value: unknown, version: number, path: string): GroundRule {
  const record = asRecord(value, path);
  knownKeys(record, ["mode", "kinds"], path);
  const mode = asString(record["mode"], `${path}.mode`);
  if (mode !== "only" && mode !== "never") {
    throw new Error(`${path}.mode is only or never, not ${mode}`);
  }
  const kinds = asArray(record["kinds"], `${path}.kinds`);
  if (kinds.length === 0 || kinds.length > MAX_GROUND_KINDS) {
    throw new Error(`${path}.kinds holds 1 to ${MAX_GROUND_KINDS} kinds, not ${kinds.length}`);
  }
  return { mode, kinds: kinds.map((kind, i) => readGroundKind(kind, version, `${path}.kinds[${i}]`)) };
}

/**
 * Reads one ground kind: a terrain and a colour, a tileset, from schema 4 one tile of a tileset, or from schema 3 a
 * zone layer and its world.
 *
 * @param value Candidate value.
 * @param version The file's schema.
 * @param path Path for error messages.
 * @returns The kind.
 * @throws Error naming the first problem's path.
 */
function readGroundKind(value: unknown, version: number, path: string): GroundKind {
  const record = asRecord(value, path);
  if ("terrain" in record) {
    knownKeys(record, ["terrain", "colour"], path);
    return { terrain: shortName(record["terrain"], `${path}.terrain`), colour: wholeIn(record["colour"], 1, MAX_COLOUR, `${path}.colour`) };
  }
  if ("tileset" in record) {
    knownKeys(record, version >= ROOM_SCHEMA ? ["tileset", "tile"] : ["tileset"], path);
    const tileset = shortName(record["tileset"], `${path}.tileset`);
    return record["tile"] === undefined ? { tileset } : { tileset, tile: asIntegerInRange(record["tile"], MAX_TILE_ID, `${path}.tile`) };
  }
  if ("zone" in record && version >= ZONE_KINDS_SCHEMA) {
    knownKeys(record, ["zone", "world"], path);
    return { zone: shortName(record["zone"], `${path}.zone`), world: shortName(record["world"], `${path}.world`) };
  }
  if (version >= ROOM_SCHEMA) {
    throw new Error(`${path} names a terrain and its colour, a tileset and optionally one of its tiles, or a zone and its world`);
  }
  throw new Error(version >= ZONE_KINDS_SCHEMA ? `${path} names a terrain and its colour, a tileset, or a zone and its world` : `${path} names a terrain and its colour, or a tileset`);
}

/**
 * Which of a set's entries a world cannot use, and why: a frame its catalogue lacks, a template the library does
 * not hold or cannot read, or a template any of whose pieces carries a behaviour. The set itself is unchanged.
 *
 * @param set The set.
 * @param lookup The world's frames and the library's templates.
 * @returns One flag per entry, in order, null for an entry the brush may use.
 */
export function brushSetFlags(set: BrushSet, lookup: BrushSetLookup): (BrushSetFlag | null)[] {
  return set.entries.map((entry) => {
    if (entry.kind === "frame") {
      return lookup.hasFrame(entry.frame) ? null : "frame-missing";
    }
    const template = lookup.template(entry.template);
    if (template === null) {
      return "template-missing";
    }
    if (template === "broken") {
      return "template-broken";
    }
    return template.pieces.some((piece) => piece.behaviour !== undefined) ? "template-behaviour" : null;
  });
}
